import { useState } from 'react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { LoadError } from '@/features/clients/LoadError'
import { formatDateTime } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { useKpiSettings, useResetKpiSettings, useSaveKpiSettings } from '@/queries/clientKpis'
import { extractErrorMessage } from '@/queries/ticketMutations'
import type { ChurnLevel, KpiBand, KpiSettings, KpiSettingsResponse, MetricCatalogEntry } from '@/types/clientKpis'

const BANDS: KpiBand[] = ['green', 'blue', 'orange', 'red', 'black']
const LEVELS: ChurnLevel[] = ['Low', 'Medium', 'High', 'Critical']
const COLUMNS = ['over 180 days', '91 to 180 days', '90 days or less']
const selectClass = 'h-8 rounded-etn-menu border border-etn-border-input bg-etn-surface px-2 text-[13px] text-etn-text'
const thClass = 'border-b border-etn-border-row px-3 py-2 text-left text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label'
const tdClass = 'border-b border-etn-border-row px-3 py-2.5 align-top'

const DEPTH_LIMITS: Record<string, { field: keyof KpiSettings['usageDepth']; label: string; unit: string }> = {
  heavyUsers: { field: 'heavyMinDays', label: 'Heavy use from (days)', unit: 'days active, at least' },
  minimalUsers: { field: 'minimalMaxDays', label: 'Minimal use up to (days)', unit: 'days active, at most' },
}

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value))
// Round away floating-point drift, so decimal weights that add up to 100 show as 100.
const sum = (values: number[]) => Math.round(values.reduce((total, value) => total + (Number.isFinite(value) ? value : 0), 0) * 100) / 100
// Shares are saved as fractions and edited as percentages.
const toShown = (value: number | undefined, percent: boolean) => (value == null || !percent ? value : Math.round(value * 100 * 1e6) / 1e6)
const toSaved = (value: number, percent: boolean) => (percent && Number.isFinite(value) ? Math.round((value / 100) * 1e6) / 1e6 : value)

function NumberField({ label, unit, value, onChange }: { label: string; unit: string; value: number | undefined; onChange: (value: number) => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <Input
        type="number"
        aria-label={label}
        className="h-8 w-[72px]"
        value={value == null || Number.isNaN(value) ? '' : value}
        onChange={event => onChange(event.target.value === '' ? Number.NaN : Number(event.target.value))}
      />
      <span className="text-[12px] text-etn-text-soft">({unit})</span>
    </span>
  )
}

function Field({ label, inputLabel = label, unit, value, onChange }: { label: string; inputLabel?: string; unit: string; value: number; onChange: (value: number) => void }) {
  return (
    <div className="flex items-center justify-between gap-4 text-[13px] text-etn-text">
      <span>{label}</span>
      <NumberField label={inputLabel} unit={unit} value={value} onChange={onChange} />
    </div>
  )
}

function Section({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-[14px] font-semibold text-etn-text">{title}</h3>
        <p className="mt-0.5 text-[12.5px] text-etn-text-soft">{description}</p>
      </div>
      {children}
    </section>
  )
}

function KpiEditor({
  kpi,
  index,
  draft,
  catalog,
  update,
  remove,
}: {
  kpi: KpiSettings['kpis'][number]
  index: number
  draft: KpiSettings
  catalog: MetricCatalogEntry[]
  update: (change: (kpi: KpiSettings['kpis'][number]) => void) => void
  remove: () => void
}) {
  const labelOf = (key: string) => catalog.find(metric => metric.key === key)?.label ?? key
  const total = sum(kpi.metrics.map(metric => metric.weight))
  const others = (key: string) => draft.kpis.filter((other, otherIndex) => otherIndex !== index && other.metrics.some(metric => metric.key === key))
  const name = kpi.name || 'New KPI'

  return (
    <fieldset aria-label={name} className="rounded-etn-card border border-etn-border-hairline bg-etn-surface">
      <div className="flex flex-wrap items-center gap-4 border-b border-etn-border-row bg-etn-surface-sunken px-4 py-3">
        <Input aria-label={`${name} name`} className="h-8 w-60 font-medium" value={kpi.name} placeholder="KPI name" onChange={event => update(item => (item.name = event.target.value))} />
        <span className="flex items-center gap-2 text-[12.5px] text-etn-text-label">
          Health score weight
          <NumberField label={`${name} health score weight`} unit="%" value={kpi.healthWeight} onChange={value => update(item => (item.healthWeight = value))} />
        </span>
        <Button size="xs" variant="ghost" className="ml-auto" onClick={remove}>
          Delete KPI
        </Button>
      </div>
      {!kpi.name.trim() && <p className="px-4 pt-2 text-[12px] text-etn-danger-text">A KPI needs a name</p>}
      <ul className="divide-y divide-etn-border-row px-4">
        {kpi.metrics.map((metric, metricIndex) => (
          <li key={metric.key} className="py-2 text-[13px]">
            <div className="flex items-center gap-3">
              <span className="flex-1 text-etn-text">{labelOf(metric.key)}</span>
              <NumberField
                label={`${labelOf(metric.key)} weight`}
                unit="%"
                value={metric.weight}
                onChange={value => update(item => (item.metrics[metricIndex].weight = value))}
              />
              <Button size="xs" variant="ghost" onClick={() => update(item => item.metrics.splice(metricIndex, 1))}>
                Remove
              </Button>
            </div>
            {others(metric.key).length > 0 && (
              <p className="mt-1 text-[12px] text-etn-warning-text">
                {`${labelOf(metric.key)} already feeds ${others(metric.key).map(other => other.name).join(', ')}. It will count more than once in the health score.`}
              </p>
            )}
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between gap-3 border-t border-etn-border-row px-4 py-2.5 text-[12.5px]">
        <select
          aria-label="Add a metric"
          className={selectClass}
          value=""
          onChange={event => event.target.value && update(item => item.metrics.push({ key: event.target.value, weight: 0 }))}
        >
          <option value="">Add a metric</option>
          {catalog
            .filter(metric => !kpi.metrics.some(entry => entry.key === metric.key))
            .map(metric => (
              <option key={metric.key} value={metric.key}>
                {metric.label}
              </option>
            ))}
        </select>
        <span className={total === 100 ? 'font-medium text-etn-text-label' : 'font-semibold text-etn-danger-text'}>{`Total ${total}%`}</span>
      </div>
    </fieldset>
  )
}

function Editor({ response }: { response: KpiSettingsResponse }) {
  const [draft, setDraft] = useState<KpiSettings>(() => clone(response.settings))
  const save = useSaveKpiSettings()
  const reset = useResetKpiSettings()
  const { catalog } = response

  const change = (apply: (settings: KpiSettings) => void) =>
    setDraft(current => {
      const next = clone(current)
      apply(next)
      return next
    })

  const healthTotal = sum(draft.kpis.map(kpi => kpi.healthWeight))
  const weightsOff = healthTotal !== 100 || draft.kpis.some(kpi => sum(kpi.metrics.map(metric => metric.weight)) !== 100)
  const unnamed = draft.kpis.some(kpi => !kpi.name.trim())

  async function submit() {
    try {
      const saved = await save.mutateAsync(draft)
      if (saved?.data) setDraft(clone(saved.data.settings))
      toast.success('KPI settings saved')
    } catch (err) {
      toast.error(extractErrorMessage(err, "Couldn't save the KPI settings"))
    }
  }

  async function restoreDefaults() {
    if (!window.confirm('Reset every KPI setting to the defaults? This rescores every client.')) return
    try {
      const saved = await reset.mutateAsync()
      setDraft(clone(saved?.data?.settings ?? response.defaults))
      toast.success('KPI settings reset to the defaults')
    } catch (err) {
      toast.error(extractErrorMessage(err, "Couldn't reset the KPI settings"))
    }
  }

  return (
    <CardContent className="space-y-8">
      <Section title="Metrics" description="Each metric turns its figure into a success percentage: 100% at the good value, 0% at the bad value, in a straight line between.">
        <div className="overflow-x-auto rounded-etn-card border border-etn-border-hairline">
          <table data-testid="kpi-metric-settings" className="w-full text-[13px]">
            <thead className="bg-etn-surface-sunken">
              <tr>
                <th className={thClass}>Metric</th>
                <th className={thClass}>How it's measured</th>
                <th className={thClass}>Good (scores 100%)</th>
                <th className={thClass}>Bad (scores 0%)</th>
                <th className={thClass}>Limit</th>
              </tr>
            </thead>
            <tbody>
              {catalog.map(metric => {
                const values = draft.metrics[metric.key] ?? { good: 0, bad: 1 }
                const setValue = (field: 'good' | 'bad', value: number) =>
                  change(next => (next.metrics[metric.key] = { ...values, [field]: toSaved(value, metric.percent) }))
                return (
                  <tr key={metric.key}>
                    <td className={cn(tdClass, 'whitespace-nowrap font-medium text-etn-text')}>{metric.label}</td>
                    <td className={cn(tdClass, 'min-w-[200px] max-w-[240px] text-[12.5px] text-etn-text-label')}>{metric.description}</td>
                    <td className={tdClass}>
                      <NumberField label={`${metric.label} good value`} unit={metric.inputUnit} value={toShown(values.good, metric.percent)} onChange={value => setValue('good', value)} />
                    </td>
                    <td className={tdClass}>
                      <NumberField label={`${metric.label} bad value`} unit={metric.inputUnit} value={toShown(values.bad, metric.percent)} onChange={value => setValue('bad', value)} />
                    </td>
                    <td className={tdClass}>
                      {metric.key in DEPTH_LIMITS && (
                        <NumberField
                          label={DEPTH_LIMITS[metric.key].label}
                          unit={DEPTH_LIMITS[metric.key].unit}
                          value={draft.usageDepth[DEPTH_LIMITS[metric.key].field]}
                          onChange={value => change(next => (next.usageDepth[DEPTH_LIMITS[metric.key].field] = value))}
                        />
                      )}
                      {metric.hasLimit && (
                        <NumberField
                          label={`${metric.label} age limit (days)`}
                          unit="days"
                          value={values.limitDays}
                          onChange={value => change(next => (next.metrics[metric.key] = { ...values, limitDays: value }))}
                        />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="KPIs" description="Each KPI is a weighted average of its metrics. Its metric weights must total 100%, and so must the health score weights across all KPIs.">
        <div className="space-y-3">
          {draft.kpis.map((kpi, index) => (
            <KpiEditor
              key={`${kpi.key}-${index}`}
              kpi={kpi}
              index={index}
              draft={draft}
              catalog={catalog}
              update={apply => change(next => apply(next.kpis[index]))}
              remove={() => window.confirm(`Delete ${kpi.name || 'this KPI'}?`) && change(next => next.kpis.splice(index, 1))}
            />
          ))}
        </div>
        <div className="flex items-center gap-4 text-[12.5px]">
          <Button size="sm" variant="outline" onClick={() => change(next => next.kpis.push({ key: '', name: '', healthWeight: 0, metrics: [] }))}>
            Add KPI
          </Button>
          <span className={healthTotal === 100 ? 'font-medium text-etn-text-label' : 'font-semibold text-etn-danger-text'}>{`Health score weights total ${healthTotal}%`}</span>
        </div>
      </Section>

      <div className="grid gap-8 sm:grid-cols-2">
        <Section title="Colour bands" description="The lowest score for each colour; below red is black.">
          <div className="grid gap-2">
            {(['green', 'blue', 'orange', 'red'] as const).map(band => (
              <Field key={band} label={`${band[0].toUpperCase()}${band.slice(1)} from`} unit="%" value={draft.bands[band]} onChange={value => change(next => (next.bands[band] = value))} />
            ))}
          </div>
        </Section>
        <Section title="Minimum data" description="Below these, a score shows Not enough data.">
          <div className="grid gap-2">
            <Field label="KPIs needed for a score" unit="KPIs" value={draft.minimums.kpis} onChange={value => change(next => (next.minimums.kpis = value))} />
            <Field label="Share of a KPI's weight needed" inputLabel="Share of a KPI needed (%)" unit="%" value={draft.minimums.kpiWeightShare} onChange={value => change(next => (next.minimums.kpiWeightShare = value))} />
          </div>
        </Section>
      </div>

      <Section title="Churn risk" description="The level for each health colour and time to renewal, raised one level when the score has fallen far enough.">
        <div className="overflow-x-auto rounded-etn-card border border-etn-border-hairline">
          <table className="w-full text-[13px]">
            <thead className="bg-etn-surface-sunken">
              <tr>
                <th className={thClass}>Health colour</th>
                {COLUMNS.map(column => (
                  <th key={column} className={thClass}>{`Renewal ${column}`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BANDS.map(band => (
                <tr key={band}>
                  <td className={cn(tdClass, 'font-medium capitalize text-etn-text')}>{band}</td>
                  {COLUMNS.map((column, columnIndex) => (
                    <td key={column} className={tdClass}>
                      <select
                        aria-label={`Churn risk for ${band}, ${column}`}
                        className={selectClass}
                        value={draft.churn.table[band][columnIndex]}
                        onChange={event => change(next => (next.churn.table[band][columnIndex] = event.target.value as ChurnLevel))}
                      >
                        {LEVELS.map(level => (
                          <option key={level}>{level}</option>
                        ))}
                      </select>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid max-w-md gap-2">
          <Field label="Raise the level when the score falls by" inputLabel="Falling score drop (points)" unit="points" value={draft.churn.fallingPoints} onChange={value => change(next => (next.churn.fallingPoints = value))} />
          <Field label="Over the last" inputLabel="Falling score period (months)" unit="months" value={draft.churn.fallingMonths} onChange={value => change(next => (next.churn.fallingMonths = value))} />
        </div>
      </Section>

      <div className="flex flex-wrap items-center gap-3 border-t border-etn-border-row pt-5">
        <Button onClick={submit} disabled={weightsOff || unnamed || save.isPending}>
          Save KPI settings
        </Button>
        <Button variant="outline" onClick={restoreDefaults} disabled={reset.isPending}>
          Reset to defaults
        </Button>
        {weightsOff && <span className="text-[12.5px] font-medium text-etn-danger-text">Weights must total 100</span>}
      </div>

      {response.changes.length > 0 && (
        <Section title="Changes" description="Who changed these settings, newest first.">
          <ul className="space-y-1.5 text-[12.5px] text-etn-text-label">
            {response.changes.map(entry => (
              <li key={`${entry.at}-${entry.byName}`}>{`${entry.byName}, ${formatDateTime(entry.at)}: ${entry.summary.join('; ')}`}</li>
            ))}
          </ul>
        </Section>
      )}
    </CardContent>
  )
}

export function KpiSettingsView() {
  const { data, isLoading, isError, refetch } = useKpiSettings()
  return (
    <div className="container mx-auto max-w-5xl px-6 pb-6">
      <Card>
        <CardHeader className="space-y-1.5">
          <CardTitle>KPIs and health score</CardTitle>
          <p className="text-sm text-muted-foreground">How each client's metrics, KPIs, health score and churn risk are scored. A change rescores every client and their history.</p>
        </CardHeader>
        {isLoading && <CardContent className="text-sm text-muted-foreground">Loading…</CardContent>}
        {isError && !data && (
          <CardContent>
            <LoadError title="KPI settings didn't load" onRetry={() => refetch()} />
          </CardContent>
        )}
        {data && <Editor key={JSON.stringify(data.data.settings)} response={data.data} />}
      </Card>
    </div>
  )
}
